<template>
  <div
    :class="{
      [`psoe-List-search-type-${searchTypeName}`]: searchTypeId,
      'psoe-List-reloading': isReload
    }"
    :data-auto-testid="`search-type-${searchTypeName}-list-type-${listType}`"
    :data-testid="`search-type-${searchTypeName}-list-type-${listType}`"
    class="psoe-List"
  >
    <SkeletonItem
      v-if="isReload"
      animation-opacity="0.8"
      class="psoe-List_Loader"
      color="transparent"
      height="100%"
      width="100%"
    />
    <OkrElementsList
      :checkins-preview-only="checkinsPreviewOnly"
      :hide-actions="hideActions"
      :objectives="objectives"
      :show-last-checkin="showLastCheckin"
      :show-last-comment="showLastComment"
      :tracking-source="EVENT_SOURCES.HOMEPAGE"
      always-show-weight
      class="psoe-List_Items"
      dim-all-weights
      has-add-kr-action
      hide-weight-icon
      item-type="primary-next"
      show-avatar-replacer
      show-grade-info
      show-interval
      show-warning-icon
      some-item-is-expandable
      transparent-filtered
      type="default-next"
      with-virtual-scroll
      @expand-item="expandItem"
      @update-elements="updateElements"
      @okr-element-deleted="$emit('okr-element-deleted')"
      @edit-element="$emit('edit-element', $event)"
      @link-jira-issue="$emit('link-jira-issue', $event)"
      @create-jira-issue="$emit('create-jira-issue', $event)"
      @link-work-item="$emit('link-work-item', $event)"
      @create-work-item="$emit('create-work-item', $event)"
      @create-key-result="$emit('create-key-result', $event)"
      @open-checkin-modal="$emit('open-checkin-modal', $event)"
    >
      <div v-if="!hideHeader" class="psoe-TitleWrapper">
        <AppTitle
          :class="{ 'psoe-List_Title-no-objectives': isEmptyObjectives }"
          :level="3"
          class="psoe-List_Title"
          disable-margin
        >
          {{ title }}
        </AppTitle>

        <div v-if="!isEmptyObjectives" class="psoe-List_RightSide">
          <div v-if="isStakeholderListType" class="psoe-GradeWrapper">
            {{ $t('dashboard.avg_progress') }}
            <ListItemGradeInfo
              :grade="objectives.length"
              :grade-info="{}"
              :icon-height="24"
              :icon-width="24"
              :options="getStakeholderOptions"
              class="psoe-GradeInfo"
              data-export-ignore
            />
          </div>

          <div class="psoe-CheckinWrapper">
            {{ $t('objectives.table_header_checkin') }}
          </div>
        </div>
      </div>

      <AppDivider
        v-if="!noDivider && !isEmptyObjectives && !loading"
        border-width="2px"
        no-margin
      />

      <template v-if="isEmptyObjectives && !loading" #no-objectives>
        <slot name="no-objectives" v-bind="{ getEmptyStateText }">
          <OkrElementsEmpty hide-hero>
            {{ getEmptyStateText }}
          </OkrElementsEmpty>
        </slot>
      </template>
    </OkrElementsList>
    <slot v-if="!isReload" name="loader" v-bind="{ loading }">
      <UserReportOkrElementsLoader v-if="loading" />
    </slot>
    <AppLoadMoreButton v-if="showLoadMoreButton && !loading" @click="loadMore" />
  </div>
</template>

<script>
import { isEmpty, isNull } from 'lodash'
import { defineComponent, inject } from 'vue'
import { computed } from 'vue'

import ObjectivesApiHandler, { SEARCH_TYPES } from '@/api/okr-elements'
import { EVENT_SOURCES } from '@/tracking/amplitude-helpers'
import { handleError } from '@/utils/error-handling'
import {
  DEFAULT_GRADE_TYPES,
  getAvailableCustomRangeParams,
  USER_LIST_TYPES
} from '@/utils/homepage'
import { homePageStateInjectionKey } from '@/utils/injection-keys'
import { UNSELECTED_INTERVAL } from '@/utils/interval'
import { HOMEPAGE_OKR_SEARCH_TYPE_IDS } from '@/utils/objectives'
import {
  isTimeRangeSelectedInsteadOfInterval,
  NEW_HP_FILTERS_KEYS
} from '@/utils/okr-elements/filters'
import { stringOrNullProp } from '@/utils/prop-validators'

import OkrElementsEmpty from '@/components/home-page/OkrElementsEmpty'
import ListItemGradeInfo from '@/components/objectives/okr-elements-list/ListItemGradeInfo'
import OkrElementsList from '@/components/objectives/okr-elements-list/OkrElementsList'
import AppDivider from '@/components/ui/AppDivider'
import AppLoadMoreButton from '@/components/ui/AppLoadMoreButton/AppLoadMoreButton'
import AppTitle from '@/components/ui/AppTitle/AppTitle'
import SkeletonItem from '@/components/ui/SkeletonLoaders/SkeletonItem'
import UserReportOkrElementsLoader from '@/components/ui/SkeletonLoaders/UserReportOkrElementsLoader'

const DEFAULT_PAYLOAD = {
  dueDateFrom: null,
  dueDateTo: null,
  levelIds: null,
  gradeTypes: null,
  typeIds: null,
  order: [1],
  childOrder: [1],
  searchType: SEARCH_TYPES.HOMEPAGE,
  searchString: null
}

const DEFAULT_LIMIT = 10

export default defineComponent({
  name: 'PersonalStatisticsOkrElementsList',
  components: {
    ListItemGradeInfo,
    AppLoadMoreButton,
    AppDivider,
    UserReportOkrElementsLoader,
    OkrElementsEmpty,
    OkrElementsList,
    AppTitle,
    SkeletonItem
  },

  props: {
    selectedIntervals: {
      type: Array,
      required: true
    },

    selectedDates: {
      type: Object,
      required: true
    },

    selectedUserOrGroup: {
      type: [String, Number],
      required: true
    },

    userSelected: {
      type: Boolean
    },

    listType: {
      type: String,
      validator: v => Object.values(USER_LIST_TYPES).includes(v),
      default: null
    },

    // TODO: handle this prop
    predefinedWorkspaceId: {
      type: Number,
      default: null
    },

    selectedUserName: {
      default: '',
      validator: v => stringOrNullProp(v)
    },

    selectedGroupName: {
      default: '',
      validator: v => stringOrNullProp(v)
    },

    hideActions: {
      type: Boolean
    },

    stakeholdersGrade: {
      type: Object,
      default: () => ({})
    },

    searchTypeId: {
      type: Number,
      default: null
    },

    showLastComment: {
      type: Boolean
    },

    showLastCheckin: {
      type: Boolean
    },

    checkinsPreviewOnly: {
      type: Boolean
    },

    noDivider: {
      type: Boolean
    },

    hideHeader: {
      type: Boolean
    }
  },

  emits: {
    'link-jira-issue': null,
    'create-jira-issue': null,
    'link-work-item': null,
    'create-work-item': null,
    'okr-element-deleted': null,
    'edit-element': null,
    'create-key-result': null,
    'open-checkin-modal': null,
    'empty-change': null
  },

  setup() {
    const homePageState = inject(homePageStateInjectionKey)
    const workspaceIds = computed(
      () => homePageState.filterValues.value[NEW_HP_FILTERS_KEYS.WORKSPACE_IDS]
    )
    const gradeTypes = computed(
      () => homePageState.filterValues.value[NEW_HP_FILTERS_KEYS.GRADE_TYPES]
    )
    return {
      workspaceIds,
      gradeTypes
    }
  },

  data() {
    return {
      loading: false,
      isReload: false,
      offset: 0,
      showLoadMoreButton: true,
      objectives: [],
      expandedItems: [],
      limit: DEFAULT_LIMIT
    }
  },

  computed: {
    EVENT_SOURCES: () => EVENT_SOURCES,

    isEmptyObjectives() {
      return isEmpty(this.objectives)
    },

    searchTypeName() {
      return this.searchTypeId === HOMEPAGE_OKR_SEARCH_TYPE_IDS.OBJECTIVES ? 'objectives' : 'krs'
    },

    payloadKeys() {
      return {
        [USER_LIST_TYPES.OWNERS]: 'ownerIds',
        [USER_LIST_TYPES.FOREIGN_OBJECTIVES]: 'ownerIds',
        [USER_LIST_TYPES.METRICS_WITHOUT_PARENTS]: 'ownerIds',
        [USER_LIST_TYPES.STAKEHOLDERS]: 'stakeholderIds',
        [USER_LIST_TYPES.METRICS_WITHOUT_KPI]: 'ownerIds'
      }
    },

    // role() {
    //   const { listType, $t } = this
    //   if (listType === null) {
    //     return $t('homepage.role.group')
    //   } else {
    //     const { OWNERS, STAKEHOLDERS, METRICS_WITHOUT_KPI } = USER_LIST_TYPES
    //     const roles = {
    //       [OWNERS]: $t('homepage.role.owner'),
    //       [STAKEHOLDERS]: $t('homepage.role.stakeholder'),
    //       [METRICS_WITHOUT_KPI]: $t('homepage.role.owner')
    //     }
    //
    //     return roles[listType]
    //   }
    // },

    // isForeignObjectiveRole() {
    //   return this.listType === USER_LIST_TYPES.FOREIGN_OBJECTIVES
    // },

    getEmptyStateText() {
      return this.$t('filter.no_objective')

      /*  if (this.isForeignObjectiveRole) {
        return this.$t('homepage.okr_elements_empty_foreign_objectives', {
          name: this.selectedUserName || this.selectedGroupName
        })
      } else {
        if (this.listType === USER_LIST_TYPES.METRICS_WITHOUT_PARENTS) {
          return this.$t('homepage.okr_elements_empty_metrics_without_parent')
        }
        return this.searchTypeId === HOMEPAGE_OKR_SEARCH_TYPE_IDS.KEY_RESULTS
          ? this.$t('homepage.okr_elements_empty_krs')
          : this.$t('homepage.okr_elements_empty', { role: this.role })
      }*/
    },

    requestPayload() {
      const {
        selectedIntervals,
        userSelected,
        selectedUserOrGroup,
        $route,
        listType,
        offset,
        limit,
        expandedItems: itemsToOpen
      } = this

      const workspaceId = !isNull(this.predefinedWorkspaceId)
        ? this.predefinedWorkspaceId
        : $route.params.workspaceId

      // let searchTypeId = this.searchTypeId
      let inForeignObjectives = null

      // if (listType === USER_LIST_TYPES.OWNERS) {
      //   searchTypeId = HOMEPAGE_OKR_SEARCH_TYPE_IDS.OBJECTIVES
      // } else if (listType === USER_LIST_TYPES.FOREIGN_OBJECTIVES) {
      //   searchTypeId = HOMEPAGE_OKR_SEARCH_TYPE_IDS.KEY_RESULTS
      //   inForeignObjectives = true
      // }
      if (listType === USER_LIST_TYPES.FOREIGN_OBJECTIVES) {
        inForeignObjectives = true
      }

      let metricSearchType
      if (listType === USER_LIST_TYPES.METRICS_WITHOUT_PARENTS) {
        metricSearchType = SEARCH_TYPES.METRICS_WITHOUT_PARENTS
      }

      const customRangeOptionsBasedOnFilters = isTimeRangeSelectedInsteadOfInterval({
        ...this.selectedDates,
        intervals: this.selectedIntervals
      })

      return {
        workspaceId,
        workspaceIds: this.workspaceIds,
        groupIds: userSelected ? null : [selectedUserOrGroup],
        [this.payloadKeys[listType]]: userSelected ? [selectedUserOrGroup] : null,
        intervalIds: customRangeOptionsBasedOnFilters.isTimeRangeSelected
          ? UNSELECTED_INTERVAL
          : selectedIntervals,

        offset,
        limit,
        itemsToOpen,

        ...DEFAULT_PAYLOAD,
        searchType: metricSearchType || this.searchTypeId,
        inForeignObjectives,
        ...getAvailableCustomRangeParams({
          selectedDates: this.selectedDates,
          intervalIds: this.selectedIntervals
        }),

        gradeTypes: isEmpty(this.gradeTypes) ? DEFAULT_GRADE_TYPES : this.gradeTypes
      }
    },

    title() {
      if (this.userSelected) {
        const titles = {
          [USER_LIST_TYPES.OWNERS]: 'homepage.objectives.owner',
          [USER_LIST_TYPES.FOREIGN_OBJECTIVES]: 'homepage.objectives.foreign_objectives',
          [USER_LIST_TYPES.METRICS_WITHOUT_PARENTS]: 'homepage.objectives.metrics_without_parents',
          [USER_LIST_TYPES.STAKEHOLDERS]: 'homepage.objectives.stakeholder',
          [USER_LIST_TYPES.METRICS_WITHOUT_KPI]: 'homepage.metrics.owner'
        }
        return this.$t(titles[this.listType], { userName: this.selectedUserName })
      } else {
        const titles = {
          [USER_LIST_TYPES.OWNERS]: 'homepage.objectives_title',
          [USER_LIST_TYPES.FOREIGN_OBJECTIVES]: 'homepage.objectives.foreign_objectives',
          [USER_LIST_TYPES.METRICS_WITHOUT_PARENTS]: 'homepage.objectives.metrics_without_parents',
          [USER_LIST_TYPES.METRICS_WITHOUT_KPI]: 'homepage.metrics_title'
        }
        return this.$t(titles[this.listType], {
          groupName: this.selectedGroupName,
          userName: this.selectedGroupName
        })
      }
    },

    isStakeholderListType() {
      return this.listType === USER_LIST_TYPES.STAKEHOLDERS
    },

    getStakeholderOptions() {
      return [
        {
          title: this.$t('okr_elements.objectives'),
          value: this.stakeholdersGrade.objectives
        },
        {
          title: this.$t('objective.key_results'),
          value: this.stakeholdersGrade.krsAndTasks
        }
      ]
    }
  },

  methods: {
    expandItem(id) {
      const { expandedItems } = this
      this.expandedItems = expandedItems.includes(id)
        ? expandedItems.filter(item => item !== id)
        : [...expandedItems, id]
    },

    async fetchObjectives(replaceElements = false) {
      this.loading = true
      this.isReload = replaceElements && !this.isEmptyObjectives
      const currentOffset = this.offset
      if (replaceElements) {
        this.limit = currentOffset || DEFAULT_LIMIT
        this.offset = 0
      }
      const objectivesApi = new ObjectivesApiHandler()
      try {
        const { elements: data } = await objectivesApi.getObjectives({ ...this.requestPayload })
        this.showLoadMoreButton = data.length >= DEFAULT_LIMIT
        if (this.showLoadMoreButton) {
          this.offset += this.limit
        }
        this.objectives = replaceElements ? data : [...this.objectives, ...data]
      } catch (error) {
        handleError({ error })
      }
      // if (replaceElements) {
      //   this.offset = currentOffset
      //   this.limit = DEFAULT_LIMIT
      // }
      this.loading = false
      this.isReload = false
      this.$emit('empty-change', this.isEmptyObjectives)
    },

    loadMore() {
      this.fetchObjectives()
    },

    /** @public */
    refreshData() {
      this.objectives = []
      this.expandedItems = []
      this.offset = 0
      this.limit = 10
      this.fetchObjectives()
    },

    /** @public */
    updateElements() {
      this.fetchObjectives(true)
    }
  }
})
</script>

<style lang="scss" scoped>
.psoe-List {
  position: relative;
  --menu-trigger-postion: static;
  --right-items-gap: 24px;

  &-reloading {
    pointer-events: none;
  }
}

.psoe-List_Loader {
  position: absolute;
  left: 0;
  top: 0;
  z-index: 1;
}
:deep(.psoe-List_Items) {
  margin: 0 var(--ob-psoe-margin-horizontal, -18px);
}

.psoe-List_Title {
  margin-bottom: 13px;
  line-height: 28px;
  font-weight: fw('semi-bold');
  font-family: $system-ui;
  color: $dark-1;

  &-no-objectives {
    color: $dark-3;
  }
}

.psoe-TitleWrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;

  --checkin-header-cell-width: calc(
    var(--right-items-gap) + var(--okr-table-checkin-cell-width) +
      var(--okr-table-row-controls-button-width)
  );
}

.psoe-GradeWrapper,
.psoe-CheckinWrapper {
  font-weight: fw('bold');
  font-size: $fs-12;
  line-height: 16px;
  color: $dark-3;
  display: flex;
  align-items: center;
  cursor: default;
}

.psoe-GradeWrapper {
  width: var(--grade-header-cell-width);
  justify-content: center;
}

.psoe-CheckinWrapper {
  padding-left: 5px;
  width: var(--checkin-header-cell-width);
}

.psoe-List {
  &:deep(.oeli-ItemContent-on-depth-0) {
    .okg-OkrGrade_MultiplierNext {
      visibility: hidden;
    }
  }
}

.psoe-GradeInfo {
  &:deep(.gi-Icon) {
    color: $dark-3;
    width: 24px;
  }
}
.psoe-List_RightSide {
  display: flex;
  flex-shrink: 0;
  gap: calc(var(--right-items-gap) * 2 + 24px);
  /// justify-content: space-between;
  /// width: calc(212px + var(--checkin-header-cell-width));
}
</style>
